export interface UaProtocolConfig {
  tag: string
  language: string
  data: string
  unity: string
  commonHeaders: string
  bridgeTag: string
  bridgeAndroidTag: string
}

/** Weelife 协议（默认优先） */
export const WEELIFE_PROTOCOL: UaProtocolConfig = {
  tag: 'weelife',
  language: 'weelife-language',
  data: 'weelife-data',
  unity: 'weelife-unity',
  commonHeaders: 'weelife-common-headers',
  bridgeTag: 'WeelifeJavascriptBridge',
  bridgeAndroidTag: 'WeelifeAppInterface'
}

/** WBBridge 协议（回退） */
export const WB_BRIDGE_PROTOCOL: UaProtocolConfig = {
  tag: 'WBBridge',
  language: 'WBBridge-language',
  data: 'WBBridge-data',
  unity: 'WBBridge-unity',
  commonHeaders: 'WBBridge-common-headers',
  bridgeTag: 'WBBridgeJSConnector',
  bridgeAndroidTag: 'WeelifeAppInterface'
}

/** 协议优先级：先 weelife，再 WBBridge */
export const UA_PROTOCOLS: UaProtocolConfig[] = [WEELIFE_PROTOCOL, WB_BRIDGE_PROTOCOL]

/** 兼容旧引用，默认指向 weelife */
export const UA_TAG = WEELIFE_PROTOCOL.tag
export const UA_LANGUAGE = WEELIFE_PROTOCOL.language
export const UA_DATA = WEELIFE_PROTOCOL.data
export const UA_UNITY = WEELIFE_PROTOCOL.unity
export const UA_COMMON_HEADERS = WEELIFE_PROTOCOL.commonHeaders

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const normalizeUa = (ua: string) => ua.toLowerCase()

export const uaIncludes = (ua: string, tag: string) => normalizeUa(ua).includes(tag.toLowerCase())

const protocolMatchesUa = (ua: string, protocol: UaProtocolConfig) => (
  uaIncludes(ua, protocol.tag) ||
  uaIncludes(ua, protocol.unity) ||
  uaIncludes(ua, protocol.language) ||
  uaIncludes(ua, protocol.data) ||
  uaIncludes(ua, protocol.commonHeaders) ||
  uaIncludes(ua, `${protocol.tag}-android`)
)

/** 根据 UA 解析当前协议，默认优先 weelife */
export const resolveUaProtocol = (ua: string = navigator.userAgent): UaProtocolConfig | null => {
  for (const protocol of UA_PROTOCOLS) {
    if (protocolMatchesUa(ua, protocol)) {
      return protocol
    }
  }
  return null
}

export const resolveBridgeProtocol = (ua: string = navigator.userAgent): UaProtocolConfig => {
  return resolveUaProtocol(ua) ?? WEELIFE_PROTOCOL
}

export const isInAppUA = (ua: string = navigator.userAgent) => (
  UA_PROTOCOLS.some(protocol => uaIncludes(ua, protocol.tag))
)

export const isIOSAppUA = (ua: string = navigator.userAgent) => (
  UA_PROTOCOLS.some(protocol => uaIncludes(ua, protocol.language))
)

export const isUnityUA = (ua: string = navigator.userAgent) => (
  UA_PROTOCOLS.some(protocol => uaIncludes(ua, protocol.unity))
)

export const isAndroidUA = (ua: string = navigator.userAgent) => (
  UA_PROTOCOLS.some(protocol => uaIncludes(ua, `${protocol.tag}-android`))
)

export const hasUaData = (ua: string = navigator.userAgent) => (
  UA_PROTOCOLS.some(protocol => uaIncludes(ua, protocol.data))
)

export const resolveUaDataKey = (ua: string = navigator.userAgent): string => {
  for (const protocol of UA_PROTOCOLS) {
    if (uaIncludes(ua, protocol.data)) {
      return protocol.data
    }
  }
  return WEELIFE_PROTOCOL.data
}

export const resolveUaCommonHeadersKey = (ua: string = navigator.userAgent): string => {
  for (const protocol of UA_PROTOCOLS) {
    if (uaIncludes(ua, protocol.commonHeaders)) {
      return protocol.commonHeaders
    }
  }
  return WEELIFE_PROTOCOL.commonHeaders
}

export const getUaDataRegex = (ua: string = navigator.userAgent) => {
  const dataKey = resolveUaDataKey(ua)
  return new RegExp(`${escapeRegExp(dataKey)}\\/({[^}]+})`)
}

export const getBridgeWindow = (): any => {
  for (const protocol of UA_PROTOCOLS) {
    const bridge = (window as any)[protocol.bridgeTag]
    if (bridge) {
      return bridge
    }
  }
  return undefined
}

export const setBridgeWindow = (bridge: any, ua: string = navigator.userAgent) => {
  const tag = resolveBridgeProtocol(ua).bridgeTag;
  (window as any)[tag] = bridge
}

export const getAndroidBridgeInterface = (): any => {
  for (const protocol of UA_PROTOCOLS) {
    const bridge = (window as any)[protocol.bridgeAndroidTag]
    if (bridge?.jsCallNative) {
      return bridge
    }
  }
  return undefined
}

export const getAppSchemeUrl = (path: string, ua: string = navigator.userAgent) => {
  const protocol = resolveUaProtocol(ua) ?? WEELIFE_PROTOCOL
  return `${protocol.tag}://${path}`
}

export const extractJsonDataByProtocols = (
  userAgent: string,
  keyGetter: (protocol: UaProtocolConfig) => string
) => {
  for (const protocol of UA_PROTOCOLS) {
    const data = extractJsonDataWithKey(userAgent, keyGetter(protocol))
    if (data) {
      return data
    }
  }
  return null
}

export const extractJsonDataWithKey = (userAgent: string, key: string): any => {
  const startIndex = userAgent.indexOf(`${key}/`)
  if (startIndex === -1) return null

  let braceCount = 0
  let jsonString = ''
  let inJson = false
  let quoteCount = 0

  for (let i = startIndex + key.length + 1; i < userAgent.length; i++) {
    const char = userAgent[i]

    if (char === '{' && quoteCount % 2 === 0) {
      braceCount++
      inJson = true
    } else if (char === '}' && quoteCount % 2 === 0) {
      braceCount--
    } else if (char === '"') {
      quoteCount++
    }

    if (inJson) {
      jsonString += char
    }

    if (inJson && braceCount === 0) {
      break
    }
  }

  const cleanJsonString = jsonString.replace(/,\s*$/, '')
  try {
    return JSON.parse(cleanJsonString)
  } catch (error) {
    return null
  }
}
